import { useMemo, useRef, useState } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { get, post, put, ApiError } from '@/lib/api';
import { formatRelative } from '@/lib/time';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';

// Product Assignment — attach personalization template zones to productConfigs
// and push the ghostfit.personalization metafield to every Shopify product
// built from each config. The list payload is lean; selecting a product
// hydrates the EXACT stored config from GET /api/personalization/products/:id.
// Zone placement here is numeric unit-range input on this page.

type ZoneRow = { id: string; templateId: string; view: string | null };
type ProductRow = {
  id: string;
  displayName: string;
  garment: string | null;
  color: string | null;
  active: boolean;
  legacyTemplateId: string | null;
  zones: ZoneRow[];
  // Server-owned sync state (recorded by the sync endpoint, survives zone saves).
  lastSyncedAt: string | null;
  lastSyncedZonesHash: string | null;
};
type Template = {
  id: string;
  displayName: string;
  fields: { id: string }[];
  physicalLocation: string;
  defaultFieldLayout?: Record<string, unknown>;
};
type Box = { x: number; y: number; w: number; h: number };
type MockupAnchor = { view: string; box: Box; viewImageIndex?: number | null };
type InputMode = 'both' | 'name-or-number';
type Zone = {
  id: string;
  label?: string | null;
  templateId: string;
  // 'both' (default) prints every field; 'name-or-number' lets the shopper pick
  // ONE field at this spot. Needs a 2+ field template to be meaningful.
  inputMode?: InputMode;
  mockupAnchor: MockupAnchor;
  fieldLayout: Record<string, unknown>;
  overrides?: Record<string, unknown>;
};
// Stored shapes are looser than what we render — normalize on hydrate.
type StoredZone = Partial<Omit<Zone, 'mockupAnchor'>> & {
  mockupAnchor?: Partial<Omit<MockupAnchor, 'box'>> & { box?: Partial<Box> };
};
type StoredPersonalization = (StoredZone & { zones?: StoredZone[] }) | null;
type DetailPayload = { id: string; displayName: string; personalization: StoredPersonalization };

const VIEWS = ['front', 'back', 'left-side', 'right-side', 'top', 'bottom'];
const DEFAULT_BOX: Box = { x: 0.4, y: 0.4, w: 0.2, h: 0.1 };
const VIEW_BY_LOCATION: Record<string, string> = { LS: 'left-side', RS: 'right-side', FB: 'back' };
const guessView = (tpl: Template | undefined) => (tpl && VIEW_BY_LOCATION[tpl.physicalLocation]) || 'front';

const centered = () => ({ x: 0.5, y: 0.5, fontSizePct: 0.6, align: 'center' });

// Template defaultFieldLayout when present, else a centered entry per field —
// matches lib/personalization-config's validateFieldLayout requirements.
function defaultLayoutFor(tpl: Template | undefined): Record<string, unknown> {
  if (tpl?.defaultFieldLayout && Object.keys(tpl.defaultFieldLayout).length) return tpl.defaultFieldLayout;
  const fields = tpl?.fields?.length ? tpl.fields : [{ id: 'name' }];
  return Object.fromEntries(fields.map((f) => [f.id, centered()]));
}

// Use stored values verbatim; fill neutral defaults only where absent so the
// numeric inputs never render undefined.
function normalizeZone(z: StoredZone, i: number, templates: Template[]): Zone {
  const anchor = z.mockupAnchor || {};
  const box = anchor.box || {};
  return {
    ...z,
    id: z.id || `zone-${i + 1}`,
    templateId: z.templateId || '',
    mockupAnchor: {
      ...anchor,
      view: anchor.view || 'front',
      box: { x: box.x ?? DEFAULT_BOX.x, y: box.y ?? DEFAULT_BOX.y, w: box.w ?? DEFAULT_BOX.w, h: box.h ?? DEFAULT_BOX.h },
    },
    fieldLayout: z.fieldLayout && Object.keys(z.fieldLayout).length
      ? z.fieldLayout
      : defaultLayoutFor(templates.find((t) => t.id === z.templateId)),
  };
}

function hydrateZones(pers: StoredPersonalization, templates: Template[]): Zone[] {
  if (!pers || typeof pers !== 'object') return [];
  if (Array.isArray(pers.zones)) return pers.zones.map((z, i) => normalizeZone(z, i, templates));
  if (pers.templateId) {
    // Legacy single-zone shape {templateId, mockupAnchor, fieldLayout} —
    // present as one zone (id 'default', matching the server's normalizeZones).
    return [normalizeZone({
      id: 'default',
      templateId: pers.templateId,
      mockupAnchor: pers.mockupAnchor,
      fieldLayout: pers.fieldLayout,
      overrides: pers.overrides,
    }, 0, templates)];
  }
  return [];
}

function nextZoneId(zones: Zone[]): string {
  const taken = new Set(zones.map((z) => z.id));
  let n = zones.length + 1;
  while (taken.has(`zone-${n}`)) n += 1;
  return `zone-${n}`;
}

// 400s carry validator `details[]`; 500s are `{error:'server_error', message}`.
function errMsg(e: unknown): string {
  if (e instanceof ApiError) {
    const body = e.body as { message?: string; error?: string; details?: string[] } | null;
    if (body?.details?.length) return body.details.join(' · ');
    return body?.message || body?.error || `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : String(e);
}

export default function PersonalizationAssign() {
  const { data: prodData, isLoading, mutate } = useSWR<{ products: ProductRow[] }>('/api/personalization/products', get);
  const { data: tplData } = useSWR<{ templates: Template[] }>('/api/personalization/templates', get);

  const templates = useMemo(
    () => [...(tplData?.templates || [])].sort((a, b) => String(a.displayName).localeCompare(String(b.displayName))),
    [tplData]);
  const tplById = useMemo(() => Object.fromEntries(templates.map((t) => [t.id, t])), [templates]);

  const [filter, setFilter] = useState('');
  const [sel, setSel] = useState<ProductRow | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [hydrating, setHydrating] = useState(false);
  // Hydrate failed — empty `zones` would render as a saveable "OFF" and one
  // Save would wipe the stored config. Block the editor until a retry works.
  const [loadFailed, setLoadFailed] = useState(false);
  // Unsaved zone edits — Sync pushes the last SAVED config, so syncing while
  // dirty would silently publish stale data.
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  // Sync state for the OPEN product: lastSyncedAt seeds from the lean row and
  // refreshes on a clean sync; `edited` flips on a successful Save after a sync
  // (the client can't hash the blob — this is the honest edited-since-sync
  // signal for the current editing session).
  const [editorSync, setEditorSync] = useState<{ lastSyncedAt: string | null; edited: boolean }>({ lastSyncedAt: null, edited: false });
  const openSeq = useRef(0);

  const products = prodData?.products || [];
  const n = filter.trim().toLowerCase();
  const rows = n
    ? products.filter((p) => [p.id, p.displayName, p.garment, p.color].some((v) => String(v || '').toLowerCase().includes(n)))
    : products;

  async function open(row: ProductRow) {
    const seq = ++openSeq.current; // ignore stale responses on rapid re-select
    setSel(row);
    setZones([]);
    setLoadFailed(false);
    setDirty(false);
    setEditorSync({ lastSyncedAt: row.lastSyncedAt, edited: false });
    setHydrating(true);
    try {
      const detail = await get<DetailPayload>(`/api/personalization/products/${encodeURIComponent(row.id)}`);
      if (openSeq.current !== seq) return;
      setZones(hydrateZones(detail.personalization, templates));
    } catch (e) {
      if (openSeq.current !== seq) return;
      setLoadFailed(true);
      toast.error(`Load failed: ${errMsg(e)}`);
    } finally {
      if (openSeq.current === seq) setHydrating(false);
    }
  }

  // All zone mutations route through here so `dirty` can't drift.
  const editZones = (next: Zone[]) => { setZones(next); setDirty(true); };

  function addZone() {
    const tpl = templates[0];
    if (!tpl) return;
    editZones([...zones, {
      id: nextZoneId(zones),
      templateId: tpl.id,
      mockupAnchor: { view: guessView(tpl), box: { ...DEFAULT_BOX } },
      fieldLayout: defaultLayoutFor(tpl),
    }]);
  }

  const setZone = (i: number, patch: Partial<Zone>) => editZones(zones.map((z, j) => (j === i ? { ...z, ...patch } : z)));
  const setBox = (i: number, k: keyof Box, v: number) =>
    setZone(i, { mockupAnchor: { ...zones[i].mockupAnchor, box: { ...zones[i].mockupAnchor.box, [k]: v } } });

  async function save() {
    if (!sel) return;
    setBusy(true);
    try {
      const body = zones.length === 0 ? {} : { zones };
      await put(`/api/personalization/products/${encodeURIComponent(sel.id)}`, body);
      setDirty(false);
      // Saved changes after a sync — the live metafield now lags the stored
      // config; show "Edited since sync" until the next clean Sync.
      setEditorSync((s) => ({ ...s, edited: s.lastSyncedAt != null }));
      toast.success(`Saved personalization for ${sel.displayName}`);
      void mutate(); // refresh list badges
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }

  async function sync() {
    if (!sel) return;
    setBusy(true);
    try {
      const r = await post<{ updated: number; failed: unknown[]; total: number; message?: string; lastSyncedAt?: string | null }>(
        `/api/personalization/products/${encodeURIComponent(sel.id)}/sync`, undefined);
      if (r.total === 0) toast.info(r.message || 'No live Shopify products for this config yet');
      else if (r.failed.length) toast.warning(`Synced ${r.updated}/${r.total}; ${r.failed.length} failed`);
      else toast.success(`Synced ${r.updated}/${r.total} Shopify products`);
      // Clean push — the server recorded sync state; mirror it locally.
      if (r.lastSyncedAt) {
        setEditorSync({ lastSyncedAt: r.lastSyncedAt, edited: false });
        void mutate(); // refresh list badges
      }
    } catch (e) {
      toast.error(`Sync failed: ${errMsg(e)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="px-7 py-6">
      <h1 className="text-[20px] font-semibold tracking-tighter">Product Assignment</h1>
      <p className="mb-4 text-[12px] text-muted">
        {products.length} product configs · attach personalization templates, then sync the metafield to live Shopify products.
      </p>

      <div className="grid items-start gap-5 lg:grid-cols-[340px_1fr]">
        {/* Left: filterable product list */}
        <div className="space-y-2">
          <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by name, id, garment, color…" />
          <div className="rounded-md bg-surface shadow-card">
            {isLoading ? (
              <div className="space-y-2 p-3"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-full" /></div>
            ) : !rows.length ? (
              <div className="p-8 text-center text-[12px] text-muted">
                {n ? `No product configs match "${filter}".` : 'No product configs found.'}
              </div>
            ) : (
              <div className="max-h-[72vh] overflow-y-auto">
                {rows.map((p) => {
                  const selected = sel?.id === p.id;
                  return (
                    <button key={p.id} onClick={() => void open(p)}
                      className={`flex w-full items-center justify-between gap-2 border-b border-divider px-3 py-2 text-left last:border-b-0 ${selected ? 'bg-primary text-white' : 'hover:bg-bg'}`}>
                      <div className="min-w-0">
                        <div className="truncate text-[12px] font-medium">{p.displayName}</div>
                        <div className={`truncate text-[11px] ${selected ? 'text-white/70' : 'text-muted'}`}>
                          {[p.garment, p.color].filter(Boolean).join(' · ') || p.id}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1">
                        {p.active === false && <Badge className="bg-red-100 text-red-700">inactive</Badge>}
                        {p.zones.length > 0 ? (
                          p.lastSyncedAt ? (
                            <Badge className="bg-success/10 text-success">Synced {formatRelative(p.lastSyncedAt)}</Badge>
                          ) : (
                            <Badge className="bg-warning/10 text-warning">Draft · {p.zones.length} zone{p.zones.length === 1 ? '' : 's'}</Badge>
                          )
                        ) : p.legacyTemplateId ? (
                          <Badge className="bg-warning/10 text-warning">legacy</Badge>
                        ) : (
                          <Badge className="bg-bg text-muted">Off</Badge>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Right: zone editor for the selected product */}
        <div className="rounded-md bg-surface p-4 shadow-card">
          {!sel ? (
            <div className="p-6 text-center text-[12px] text-muted">Select a product to edit its personalization.</div>
          ) : loadFailed ? (
            // Hydrate failed — no editor, no Save/Sync: an empty editor here
            // looks like "OFF" and saving it would wipe the stored config.
            <div>
              <div className="mb-1 min-w-0">
                <h2 className="truncate text-[14px] font-semibold">{sel.displayName}</h2>
                <div className="font-mono text-[10px] text-subtle">{sel.id}</div>
              </div>
              <p className="mb-3 text-[12px] text-red-600">
                {"Couldn't load this product's personalization — not showing live data."}
              </p>
              <Button size="sm" variant="secondary" disabled={hydrating} onClick={() => void open(sel)}>Retry</Button>
            </div>
          ) : (
            <>
              <div className="mb-1 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <h2 className="truncate text-[14px] font-semibold">{sel.displayName}</h2>
                    {!hydrating && (
                      zones.length === 0 ? (
                        <Badge className="shrink-0 bg-bg text-muted">Off</Badge>
                      ) : editorSync.edited && editorSync.lastSyncedAt ? (
                        <Badge className="shrink-0 bg-warning/10 text-warning">Edited since sync</Badge>
                      ) : editorSync.lastSyncedAt ? (
                        <Badge className="shrink-0 bg-success/10 text-success">Synced {formatRelative(editorSync.lastSyncedAt)}</Badge>
                      ) : (
                        <Badge className="shrink-0 bg-warning/10 text-warning">Draft</Badge>
                      )
                    )}
                  </div>
                  <div className="font-mono text-[10px] text-subtle">{sel.id}</div>
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button size="sm" variant="secondary" disabled={hydrating || !templates.length} onClick={addZone}>+ zone</Button>
                  <Button size="sm" disabled={busy || hydrating} onClick={() => void save()}>{busy ? 'Working…' : 'Save'}</Button>
                  <span title={dirty ? 'Save your changes first — Sync pushes the last saved config' : undefined}>
                    <Button size="sm" variant="secondary" disabled={busy || hydrating || dirty}
                      onClick={() => void sync()}>Sync to Shopify</Button>
                  </span>
                </div>
              </div>
              <p className="mb-3 text-[11px] text-muted">
                Box values are unit-range (0–1) on the chosen view image. For drag-precise placement use the Product Config dashboard — both edit the same config.
              </p>

              {hydrating ? (
                <div className="space-y-2"><Skeleton className="h-16 w-full" /></div>
              ) : zones.length === 0 ? (
                <p className="text-[12px] text-muted">Personalization is OFF for this product (no zones). Save to persist.</p>
              ) : (
                <div className="space-y-3">
                  {zones.map((z, i) => (
                    <div key={i} className="rounded-md border border-border p-3">
                      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                        <label className="block">
                          <span className="block text-[10px] uppercase tracking-[0.04em] text-muted">template</span>
                          <select value={z.templateId} className={inputCls}
                            onChange={(e) => {
                              const tpl = tplById[e.target.value];
                              // Re-derive fieldLayout for the new template.
                              setZone(i, { templateId: e.target.value, fieldLayout: defaultLayoutFor(tpl) });
                            }}>
                            {z.templateId && !tplById[z.templateId] && (
                              <option value={z.templateId}>{z.templateId} (missing)</option>
                            )}
                            {templates.map((t) => <option key={t.id} value={t.id}>{t.displayName}</option>)}
                          </select>
                        </label>
                        <label className="block">
                          <span className="block text-[10px] uppercase tracking-[0.04em] text-muted">view</span>
                          <select value={z.mockupAnchor.view} className={inputCls}
                            onChange={(e) => setZone(i, { mockupAnchor: { ...z.mockupAnchor, view: e.target.value } })}>
                            {VIEWS.map((v) => <option key={v} value={v}>{v}</option>)}
                          </select>
                        </label>
                        <label className="block">
                          <span className="block text-[10px] uppercase tracking-[0.04em] text-muted">zone id</span>
                          <input value={z.id} onChange={(e) => setZone(i, { id: e.target.value })} className={inputCls} />
                        </label>
                        <label className="block">
                          <span className="block text-[10px] uppercase tracking-[0.04em] text-muted">input mode</span>
                          <select value={z.inputMode || 'both'} className={inputCls}
                            onChange={(e) => setZone(i, { inputMode: e.target.value as InputMode })}>
                            <option value="both">Name &amp; Number</option>
                            <option value="name-or-number">Name or Number</option>
                          </select>
                        </label>
                      </div>
                      {z.inputMode === 'name-or-number' && (tplById[z.templateId]?.fields?.length ?? 0) < 2 && (
                        <p className="mt-1 text-[10px] text-warning">
                          “Name or Number” needs a template with 2+ fields — this template has fewer, so the shopper sees all fields.
                        </p>
                      )}
                      <div className="mt-2 grid grid-cols-[1fr_1fr_1fr_1fr_auto] items-end gap-2">
                        {(['x', 'y', 'w', 'h'] as const).map((k) => (
                          <label key={k} className="block">
                            <span className="block text-[10px] uppercase tracking-[0.04em] text-muted">{k}</span>
                            <input type="number" step={0.01} min={0} max={1}
                              value={Number.isFinite(z.mockupAnchor.box[k]) ? z.mockupAnchor.box[k] : ''}
                              onChange={(e) => setBox(i, k, Number(e.target.value))}
                              className={inputCls} />
                          </label>
                        ))}
                        <button type="button"
                          className="h-[30px] rounded-sm border border-border px-2 text-[11px] text-red-600 hover:bg-bg"
                          onClick={() => editZones(zones.filter((_, j) => j !== i))}>
                          remove
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const inputCls = 'mt-1 w-full rounded-sm border border-border bg-surface px-2 py-1.5 text-[12px]';
